import React, { useCallback, useEffect, useState } from "react";
import {
  KeyRound, Loader2, Plus, RefreshCw, Search, ShieldOff, UserCog, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { PermissionMatrix } from "./settings/PermissionMatrix";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("") + "@1";
};

const blank = {
  name: "", email: "", phone: "", designation: "", department: "", role: "sales",
  password: "", permission_overrides: null, monthly_target: 0, monthly_salary: 0, active: true,
  send_credentials: true,
};

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ modules: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [revoke, setRevoke] = useState(null);
  const [customPerms, setCustomPerms] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, m] = await Promise.all([api.get("/staff"), api.get("/roles"), api.get("/roles/meta")]);
      setRows(u.data); setRoles(r.data); setMeta(m.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...blank, password: genPassword() });
    setCustomPerms(false);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...blank, ...u, password: "" });
    setCustomPerms(!!u.permission_overrides && Object.keys(u.permission_overrides).length > 0);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, permission_overrides: customPerms ? (form.permission_overrides || {}) : {} };
      if (editing) {
        const { password, email, send_credentials, ...rest } = payload;
        await api.patch(`/staff/${editing.id}`, rest);
        toast.success("Staff member updated");
      } else {
        await api.post("/staff", payload);
        toast.success("Staff account created");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/staff/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Account deactivated" : "Account activated");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resetPassword = async () => {
    try {
      await api.post(`/staff/${pwTarget.id}/reset-password`, { password: newPw, notify_user: true });
      toast.success("Password reset and emailed to the staff member");
      setPwTarget(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doRevoke = async () => {
    try {
      await api.delete(`/staff/${revoke.id}`);
      toast.success("Login access revoked");
      setRevoke(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = rows.filter((r) =>
    !q || [r.name, r.email, r.designation, r.department].join(" ").toLowerCase().includes(q.toLowerCase()));

  const roleName = (key) => roles.find((r) => r.key === key)?.name || key;

  const columns = [
    {
      key: "user", label: "Staff member",
      render: (r) => (
        <div className="flex items-center gap-3">
          <UserAvatar key={r.email} name={r.name} src={r.avatar} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{r.name}</p>
            <p className="truncate text-xs text-muted-foreground">{r.email}</p>
          </div>
        </div>
      ),
    },
    { key: "role", label: "Role", render: (r) => <StatusBadge status={roleName(r.role)} tone={r.role === "admin" ? "primary" : "info"} /> },
    { key: "designation", label: "Designation", render: (r) => <div><p className="text-sm">{r.designation || "\u2014"}</p><p className="text-xs text-muted-foreground">{r.department}</p></div>, hideMobile: true },
    { key: "last_login", label: "Last login", render: (r) => <span className="text-xs text-muted-foreground">{r.last_login ? fdatetime(r.last_login) : "Never"}</span>, hideMobile: true },
    { key: "created_at", label: "Created", render: (r) => <span className="text-xs text-muted-foreground">{fdatetime(r.created_at)}</span>, hideMobile: true },
    {
      key: "active", label: "Active",
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`toggle-${r.email}`} />
        </div>
      ),
    },
    {
      key: "actions", label: "", align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="iconSm" variant="outline" title="Reset password" onClick={() => { setPwTarget(r); setNewPw(genPassword()); }}>
            <KeyRound className="h-4 w-4" />
          </Button>
          <Button size="iconSm" variant="outline" title="Revoke access" onClick={() => setRevoke(r)}>
            <ShieldOff className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UserCog}
        title="Staff Management"
        subtitle="Create logins, assign roles and control exactly what each person can do."
        actions={<Button onClick={openNew} data-testid="new-staff"><Plus className="h-4 w-4" /> New staff account</Button>}
      />

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search staff" />
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable columns={columns} rows={filtered} onRowClick={openEdit}
          empty={<EmptyState icon={UserCog} title="No staff accounts" description="Create the first staff login to get started." />} />
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? `Edit ${editing.name}` : "New staff account"}</DialogTitle>
            <DialogDescription>Role decides the default access. You can fine tune it per person below.</DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name" required>
                <Input required value={form.name} data-testid="staff-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Email address" required>
                <Input type="email" required disabled={!!editing} value={form.email} data-testid="staff-email"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Phone number"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="Designation"><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></Field>
              <Field label="Department"><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
              <Field label="Monthly sales target (INR)" hint="Shown as a progress bar on their dashboard.">
                <Input type="number" min="0" value={form.monthly_target || 0} data-testid="staff-target"
                  onChange={(e) => setForm({ ...form, monthly_target: Number(e.target.value) })} />
              </Field>
              <Field label="Monthly salary (INR)" hint="Used only to work out employee cost per invoice. Visible to admins only.">
                <Input type="number" min="0" value={form.monthly_salary || 0} data-testid="staff-salary"
                  onChange={(e) => setForm({ ...form, monthly_salary: Number(e.target.value) })} />
              </Field>
              <Field label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            {!editing && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Initial password" required>
                  <div className="flex gap-2">
                    <Input required minLength={8} value={form.password} data-testid="staff-password"
                      onChange={(e) => setForm({ ...form, password: e.target.value })} />
                    <Button type="button" variant="outline" size="icon" title="Generate" onClick={() => setForm({ ...form, password: genPassword() })}>
                      <Wand2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Field>
                <label className="flex items-end gap-2.5 pb-2 text-sm">
                  <Checkbox checked={form.send_credentials} onCheckedChange={(v) => setForm({ ...form, send_credentials: !!v })} />
                  Email these credentials to the staff member
                </label>
              </div>
            )}

            <div className="rounded-xl border border-border p-4">
              <label className="flex items-center justify-between gap-3">
                <span>
                  <span className="block text-sm font-semibold">Custom permissions for this person</span>
                  <span className="block text-xs text-muted-foreground">Leave off to use the role defaults.</span>
                </span>
                <Switch checked={customPerms} onCheckedChange={setCustomPerms} data-testid="custom-perms" />
              </label>
              {customPerms && (
                <div className="mt-4">
                  <PermissionMatrix
                    modules={meta.modules} actions={meta.actions}
                    value={form.permission_overrides || roles.find((r) => r.key === form.role)?.permissions || {}}
                    onChange={(v) => setForm({ ...form, permission_overrides: v })}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="staff-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing ? "Save changes" : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwTarget} onOpenChange={(v) => !v && setPwTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Reset password</DialogTitle>
            <DialogDescription>A new password will be set for {pwTarget?.name} and emailed to them.</DialogDescription>
          </DialogHeader>
          <Field label="New password">
            <div className="flex gap-2">
              <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} />
              <Button type="button" variant="outline" size="icon" onClick={() => setNewPw(genPassword())}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)}>Cancel</Button>
            <Button onClick={resetPassword} disabled={newPw.length < 8}>Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!revoke} onOpenChange={(v) => !v && setRevoke(null)}
        title="Revoke login access?"
        description={`${revoke?.name} will be deactivated immediately and will not be able to sign in. Their records stay intact.`}
        confirmLabel="Revoke access" destructive onConfirm={doRevoke}
      />
    </div>
  );
}
